import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useBankAccounts } from "../../hooks/use-bank-accounts";
import type { Bank } from "../../hooks/use-bank-accounts";
import { Select } from "../ui/Select/Select";
import styles from "./BankInfoClauseEditor.module.css";

interface Row {
	id: number;
	bankName: string;
	currency: string;
}

let rowIdSeq = 0;
function nextRowId() {
	rowIdSeq += 1;
	return rowIdSeq;
}

function buildText(rows: Row[], banks: Bank[]): string {
	return rows
		.map((r) => {
			const bank = banks.find((b) => b.name === r.bankName);
			const account = bank?.accounts.find((a) => a.currency === r.currency);
			return bank && account ? `${bank.name} ${account.currency} / ${account.iban}` : null;
		})
		.filter((line): line is string => Boolean(line))
		.join("\n");
}

function parseRows(text: string, banks: Bank[]): Row[] {
	const rows: Row[] = [];
	for (const rawLine of text.split("\n")) {
		const line = rawLine.trim();
		if (!line) continue;
		const slashIdx = line.lastIndexOf("/");
		if (slashIdx === -1) continue;
		const left = line.slice(0, slashIdx).trim();
		const match = left.match(/^(.*\S)\s+(\S+)$/);
		if (!match) continue;
		const namePart = match[1] ?? "";
		const currencyToken = match[2] ?? "";
		const bank = banks.find((b) => b.name.trim().toUpperCase() === namePart.trim().toUpperCase());
		if (!bank || bank.accounts.length === 0) continue;
		const account =
			bank.accounts.find((a) => a.currency.toUpperCase() === currencyToken.toUpperCase()) ??
			bank.accounts.find((a) => a.currency.toUpperCase() === "TL") ??
			bank.accounts[0];
		if (!account) continue;
		rows.push({ id: nextRowId(), bankName: bank.name, currency: account.currency });
	}
	return rows;
}

function defaultRows(banks: Bank[]): Row[] {
	const ziraat = banks.find((b) => b.name.toUpperCase().includes("ZİRAAT BANKASI")) ?? banks[0];
	if (!ziraat || ziraat.accounts.length === 0) return [];
	return ziraat.accounts.slice(0, 3).map((a) => ({
		id: nextRowId(),
		bankName: ziraat.name,
		currency: a.currency,
	}));
}

interface BankInfoClauseEditorProps {
	value: string;
	onChange: (text: string) => void;
}

export function BankInfoClauseEditor({ value, onChange }: BankInfoClauseEditorProps) {
	const { data: banks } = useBankAccounts();
	const [rows, setRows] = useState<Row[] | null>(null);

	useEffect(() => {
		if (!banks || rows !== null) return;
		const parsed = parseRows(value, banks);
		const initial = parsed.length > 0 ? parsed : defaultRows(banks);
		setRows(initial);
		const text = buildText(initial, banks);
		if (text !== value) onChange(text);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [banks, value]);

	if (!banks || rows === null) {
		return <p className={styles.hint}>Banka listesi yükleniyor…</p>;
	}

	const commit = (nextRows: Row[]) => {
		setRows(nextRows);
		onChange(buildText(nextRows, banks));
	};

	const updateRow = (id: number, patch: Partial<Row>) => {
		commit(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const removeRow = (id: number) => {
		commit(rows.filter((r) => r.id !== id));
	};

	const addRow = () => {
		commit([...rows, { id: nextRowId(), bankName: "", currency: "" }]);
	};

	return (
		<div className={styles.rows}>
			{rows.map((row) => {
				const bank = banks.find((b) => b.name === row.bankName);
				const currencyOptions = bank?.accounts.map((a) => a.currency) ?? [];
				return (
					<div key={row.id} className={styles.row}>
						<Select
							className={styles.bankSelect}
							value={row.bankName}
							onChange={(e) => updateRow(row.id, { bankName: e.target.value, currency: "" })}
							options={[
								{ value: "", label: "Seçiniz" },
								...banks.map((b) => ({ value: b.name, label: b.name })),
							]}
						/>
						<Select
							className={styles.currencySelect}
							value={row.currency}
							onChange={(e) => updateRow(row.id, { currency: e.target.value })}
							disabled={!row.bankName}
							options={[
								{ value: "", label: "Seçiniz" },
								...currencyOptions.map((c) => ({ value: c, label: c })),
							]}
						/>
						{rows.length > 1 && (
							<button
								type="button"
								className={styles.removeButton}
								onClick={() => removeRow(row.id)}
								aria-label="Bu bankayı kaldır"
							>
								<X size={14} />
							</button>
						)}
					</div>
				);
			})}
			<button type="button" className={styles.addButton} onClick={addRow}>
				+ Banka Ekle
			</button>
		</div>
	);
}
